// v2/services/client.js
//
// Cliente HTTP que reusa la instancia axios existente (libs/axios.js)
// pero con utilidades específicas de v2:
//  - Soporta header X-Force-V2 para QA forzar el flujo nuevo.
//  - Mapea los códigos de error del backend (TV_API_NO_RESPONDE,
//    COT_EXPIRADA, etc.) a mensajes localizados en español.

import instance from '../apis/app';

const STORAGE_KEY_FORCE_V2 = 'inscripcion_v2_force';
const STORAGE_KEY_FORCE_V1 = 'inscripcion_v2_force_v1';

/**
 * Header bypass para QA: localStorage.setItem('inscripcion_v2_force','1')
 * fuerza v2 sin importar el bucketing del backend. Idem para v1.
 */
function getForceHeaders() {
  const headers = {};
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (window.localStorage.getItem(STORAGE_KEY_FORCE_V2) === '1') {
        headers['X-Force-V2'] = 'true';
      } else if (window.localStorage.getItem(STORAGE_KEY_FORCE_V1) === '1') {
        headers['X-Force-V1'] = 'true';
      }
    }
  } catch { /* ignore SSR */ }
  return headers;
}

export async function v2Get(path, opts = {}) {
  return instance.get(path, {
    ...opts,
    headers: { ...(opts.headers || {}), ...getForceHeaders() }
  });
}

export async function v2Post(path, data, opts = {}) {
  return instance.post(path, data, {
    ...opts,
    headers: { ...(opts.headers || {}), ...getForceHeaders() }
  });
}

export async function v2Put(path, data, opts = {}) {
  return instance.put(path, data, {
    ...opts,
    headers: { ...(opts.headers || {}), ...getForceHeaders() }
  });
}

/**
 * Catálogo de mensajes para los códigos de error del backend v2.
 * Se usa en el toast/alert de cada componente.
 */
export const MENSAJES_ERROR = {
  // Tarjeta Vecino
  TV_RUT_INVALIDO: 'El RUT no tiene un formato válido (12345678-9).',
  TV_API_NO_RESPONDE: 'No pudimos verificar tu Tarjeta Vecino. Por favor intenta de nuevo en unos segundos.',
  // Cotización
  COT_NO_EXISTE: 'La cotización no existe o ya no es válida.',
  COT_EXPIRADA: 'Tu cotización expiró. Vuelve al carrito y confirma de nuevo.',
  COT_CONSUMIDA: 'Esta cotización ya fue usada.',
  COT_HASH_MISMATCH: 'La cotización no coincide. Vuelve al carrito.',
  COT_INPUT_INVALIDO: 'Faltan datos para cotizar.',
  // Cupos
  CUPO_AGOTADO: 'Lo sentimos, el cupo se agotó mientras estabas comprando.',
  CUPO_INCONSISTENTE: 'Hubo un problema con la reserva del cupo, intenta de nuevo.',
  RESERVA_NO_ACTIVA: 'Tu reserva de cupo ya no está activa.',
  RESERVAS_INSUFICIENTES: 'Faltan reservas de cupo. Vuelve al carrito.',
  HORARIO_NO_EXISTE: 'El horario seleccionado no existe.',
  // Pago
  WEBPAY_CREATE_FALLO: 'No pudimos iniciar el pago con Webpay. Intenta de nuevo.',
  WEBPAY_COMMIT_FALLO: 'Webpay no respondió la confirmación. Tu pago puede estar en proceso.',
  WEBPAY_INPUT_INVALIDO: 'Datos del pago inválidos.',
  PAGO_NO_EXISTE: 'No encontramos tu pago.',
  PAGO_INPUT_INVALIDO: 'Faltan datos del pago.',
  ORDEN_NO_EXISTE: 'No encontramos tu orden.',
  // Idempotencia
  IDEM_EN_CURSO: 'Tu pago se está procesando, espera unos segundos.',
  IDEM_TOKEN_REQUERIDO: 'Token de pago no recibido.',
  // Venta presencial
  VP_NO_EXISTE: 'No encontramos la venta.',
  VP_ESTADO_INVALIDO: 'La venta no está en un estado válido para pagar.',
  VP_SIN_MONTO: 'La venta no tiene monto a cobrar.',
  VP_RESERVA_EXPIRADA: 'La reserva de cupos expiró. Inicia la venta de nuevo.',
  VP_PAGO_INPUT_INVALIDO: 'Faltan datos para iniciar el pago presencial.',
  // Admin
  REV_NO_EXISTE: 'No encontramos esa entrada.',
  REV_NO_REQUERIDA: 'Esta entrada no requiere revisión.',
  REV_YA_RESUELTA: 'Esta entrada ya fue resuelta.',
  REV_INPUT_INVALIDO: 'Faltan datos para resolver.',
  // Genérico
  V2_NOT_ENABLED: 'Esta función no está disponible para tu cuenta.',
  V2_NOT_IMPLEMENTED: 'Función en construcción, vuelve más tarde.',
  INTERNAL_ERROR: 'Algo salió mal, intenta de nuevo en unos segundos.'
};

/**
 * Extrae mensaje legible de cualquier error de axios sobre /v2/*.
 */
export function mensajeDeError(err, fallback = 'Algo salió mal') {
  const code = err?.response?.data?.code || err?.code;
  if (code && MENSAJES_ERROR[code]) return MENSAJES_ERROR[code];
  return err?.response?.data?.error || err?.message || fallback;
}

/**
 * Devuelve el código de error normalizado o null si no es uno conocido.
 */
export function codigoError(err) {
  return err?.response?.data?.code || err?.code || null;
}

/**
 * Normaliza el error de una llamada axios para relanzarlo desde los servicios.
 *
 * Antes cada servicio hacía `throw error.response.data` sin optional chaining.
 * Cuando la petición no llega a obtener respuesta —red caída, timeout, CORS,
 * servidor abajo— `error.response` es `undefined`, así que ese throw reventaba
 * con un TypeError y la interfaz terminaba mostrándole al usuario:
 *
 *     Cannot read properties of undefined (reading 'data')
 *
 * Además, como los componentes hacen `notify.error(error.message || 'texto en
 * español')` y `error.message` siempre viene con algo, el texto en español de
 * respaldo no se usaba nunca.
 *
 * Esta función mantiene el contrato previo (si el backend respondió, se relanza
 * su payload tal cual) y solo interviene cuando no hubo respuesta.
 */
export function errorDeApi(
  error,
  fallback = 'No pudimos conectar con el servidor. Revisa tu conexión e intenta nuevamente.'
) {
  const data = error?.response?.data;
  if (data) return data;

  // Sin respuesta HTTP: el mensaje de axios ("Network Error", "timeout of
  // 0ms exceeded") no le sirve a nadie, así que se reemplaza por uno legible.
  return {
    message: error?.response ? mensajeDeError(error, fallback) : fallback,
    code: codigoError(error),
    status: error?.response?.status ?? null,
  };
}
